import { Link } from "@tanstack/react-router";

import { SettingsPageContainer, SettingsSection } from "../settings/settingsLayout";
import { allPluginDetailMetadata, type PluginDetailMetadata } from "./pluginMetadata";
import { PluginsPageChrome } from "./PluginPageSections";
import {
  PLUGIN_ROW_CLASS,
  PLUGIN_ROW_LINK_CLASS,
  PluginIconTile,
  PluginRowText,
} from "./pluginRowChrome";

interface PluginSkillListItem {
  readonly id: string;
  readonly plugin: PluginDetailMetadata;
  readonly name: string;
  readonly description: string;
}

function pluginSkillListItems(): ReadonlyArray<PluginSkillListItem> {
  return allPluginDetailMetadata().flatMap((plugin) =>
    plugin.skills.map((skill) => ({
      id: `${plugin.id}:${skill.id}`,
      plugin,
      name: skill.name,
      description: skill.description,
    })),
  );
}

interface PluginSkillRowProps {
  readonly skill: PluginSkillListItem;
}

/**
 * A skill row, sharing the plugin row's chrome so the two pages behind one view
 * switcher do not read as two different products. There is no control column
 * here — the switch that owns a skill lives on its plugin's detail page — so
 * the row is a single link across the whole width.
 */
function PluginSkillRow({ skill }: PluginSkillRowProps) {
  return (
    <div className={`${PLUGIN_ROW_CLASS} transition-colors hover:bg-accent`}>
      <Link
        to="/plugins/$pluginId"
        params={{ pluginId: skill.plugin.id }}
        className={PLUGIN_ROW_LINK_CLASS}
        aria-label={`Open ${skill.plugin.title}`}
      >
        <PluginIconTile pluginId={skill.plugin.id} />
        <PluginRowText name={skill.name} description={skill.description} />
      </Link>
    </div>
  );
}

export function PluginSkillsPage() {
  const skills = pluginSkillListItems();

  return (
    <main className="flex h-[calc(100dvh-var(--forge-app-top-bar-height))] min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
      <PluginsPageChrome activeView="skills" />
      <SettingsPageContainer>
        <SettingsSection
          title="Available skills"
          headerAction={
            <span className="text-xs tabular-nums text-muted-foreground">{skills.length}</span>
          }
        >
          {skills.map((skill) => (
            <PluginSkillRow key={skill.id} skill={skill} />
          ))}
        </SettingsSection>
      </SettingsPageContainer>
    </main>
  );
}
